// React
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
// Helpers
import { isFunction } from "@mefisto/utils";
// Framework
import { useMounted } from "hooks";
import { usePortal } from "stack";
import { Tooltip } from "ui";

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const TimeAgo = ({
  timestamp,
  tooltipFormat = "LLL (z)",
  timeout = 15000,
  children,
}) => {
  // Framework
  const { time, localization } = usePortal();
  const { callWhenMounted } = useMounted();
  // State
  const [locale, setLocale] = useState(localization.locale);
  // Callback
  const date = useMemo(() => {
    return time.fromFormat(timestamp);
  }, [time, timestamp]);
  const timeAgo = useMemo(() => {
    if (locale) {
      return time.timeAgo(date.valueOf());
    }
  }, [time, date, locale]);
  const formattedTimestamp = useMemo(() => {
    return date.format(tooltipFormat);
  }, [date, tooltipFormat]);
  // State
  const [value, setValue] = useState(timeAgo);
  // Effects
  useEffect(() => {
    const handleInterval = () => setValue(timeAgo);
    const interval = setInterval(handleInterval, timeout);
    handleInterval();
    return () => clearInterval(interval);
  }, [timeout, timeAgo]);
  useEffect(() => {
    return localization.onChange(
      callWhenMounted((localization) => {
        setLocale(localization.locale);
      }),
      true
    );
  }, [callWhenMounted, localization]);
  // Render
  return (
    <Tooltip title={formattedTimestamp}>
      <div>{isFunction(children) ? children(value) : value}</div>
    </Tooltip>
  );
};

TimeAgo.propTypes = {
  timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tooltipFormat: PropTypes.string,
  timeout: PropTypes.number,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
};

export default TimeAgo;
